import Usercentrics from "../Usercentrics/usercentrics";

/**
 * Opens modals for videos.
 * Requirements:
 * - A trigger, usually a button. Must have the following attributes:
 *   - data-js-video-modal - Required for initialization
 *   - data-js-video-modal-target - ID of the associated modal element
 * - A modal
 *   - Somewhat DOM structure independent, but requires a root node (usually the backdrop, which contains the modal itself)
 *    and an inner iframe
 *   - Must have the following attribute:
 *     - data-js-video-modal-id - ID format does not matter, but associated trigger must refer to the same exact ID
 *   - The inner iframe must have the following attribute:
 *     - data-js-video-modal-src - The full source to the embeddable video, used to set the iframe src attribute only when applicable
 *        (i.e. when cookies are accepted)
 *   - Optional attribute:
 *     - data-js-video-modal-position="body" - move the modal to the end of the body element to avoid interfering with focus dependent
 *        parent elements, e.g. menu sections.
 *  - A cookie consent information modal
 *   - Must have the following attributes:
 *     - data-js-cookie-consent-modal-id - ID format does not matter, but associated trigger must refer to the same exact ID
 *   - Must have a consent button with the following attributes:
 *     - data-js-cookie-consent-button-id - ID format does not matter, but associated trigger must refer to the same exact ID
 */
export const videoModal = () => {
  const modalButtons = document.querySelectorAll("[data-js-video-modal]");

  if (!modalButtons?.length) {
    return;
  }

  for (const modalButton of modalButtons) {
    const targetModalId = modalButton.getAttribute("data-js-video-modal-target");

    if (!targetModalId?.length) {
      console.warn("Video modal trigger found, but no target modal ID is set.");
      continue;
    }

    const videoModal = document.querySelector(
      `[data-js-video-modal-id="${targetModalId}"]`
    ) as HTMLElement | undefined;

    if (!videoModal) {
      console.warn(
        "Video modal trigger with associated modal ID found, but no actual modal with the corresponding ID was found."
      );
      continue;
    }

    const videoIframe = videoModal.querySelector("iframe");

    if (!videoIframe) {
      console.warn(`Video modal with ID ${targetModalId} contains no iframe.`);
      continue;
    }

    const videoIframeSrc = videoIframe.getAttribute("data-js-video-modal-src");

    if (!videoIframeSrc?.length) {
      console.warn(
        `Iframe in video modal with ID ${targetModalId} has no/invalid source set in data-src.`
      );
      continue;
    }

    const cookieModal = document.querySelector(
      `[data-js-cookie-consent-modal-id="${targetModalId}"]`
    ) as HTMLElement | undefined;

    if (!cookieModal) {
      console.warn(
        "Video cookie modal trigger with associated modal ID found, but no actual cookie modal with the corresponding ID was found."
      );
      continue;
    }

    const cookieConsentButton = document.querySelector(
      `[data-js-cookie-consent-button-id="${targetModalId}"]`
    ) as HTMLElement | undefined;

    if (!cookieConsentButton) {
      console.warn(
        `Video cookie consent modal with ID ${targetModalId} contains no consent button.`
      );
      continue;
    }

    modalButton.addEventListener("click", (e: Event) => {
      e.preventDefault();
      if (Usercentrics.getDataProcessingConsent("Vimeo")) {
        if (videoModal.dataset.jsVideoModalPosition === "body") {
          moveToBody(videoModal);
        }

        videoModal.classList.remove("hidden");
        videoIframe.setAttribute("src", videoIframeSrc);
        return;
      }

      // Inform visitor about required cookies and request consent.
      cookieModal.classList.remove("hidden");
    });

    cookieConsentButton.addEventListener("click", (e: Event) => {
      e.preventDefault();
      Usercentrics.acceptServices(["Vimeo", "YouTube Video"]);
      cookieModal.classList.add("hidden");

      (modalButton as HTMLElement).click();
    });

    // Root node is the backdrop for the video
    videoModal.addEventListener("click", (e: Event) => {
      e.stopPropagation();
      videoModal.classList.add("hidden");
      videoIframe.setAttribute("src", "");
    });

    window.addEventListener("keyup", (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        videoModal.classList.add("hidden");
        videoIframe.setAttribute("src", "");
      }
    });
  }

  const moveToBody = (videoModal: HTMLElement) => {
    if (videoModal.parentElement?.tagName === "BODY") {
      return;
    }

    // move modal to the body element
    const fragment = document.createDocumentFragment();
    fragment.appendChild(videoModal);
    document.body.appendChild(fragment);
  };
};
